/**
 * Video Embed Utilities — the single source of truth for turning a
 * user-supplied video URL into a safe iframe `src`.
 *
 * SECURITY: an arbitrary user-supplied iframe `src` is a clickjacking /
 * embed-abuse vector, so we NEVER embed a raw URL. Every URL is validated
 * against a fixed host allowlist (YouTube / Vimeo / Twitch / Facebook /
 * Dailymotion) AND coerced to the provider's canonical `/embed/` form. A URL
 * whose host is not on the allowlist, or that is not https, is rejected
 * outright (returns `null`, never a pass-through of the input).
 *
 * `resolveVideoEmbed` is the canonical resolver. The older
 * `getEmbedUrl` / `getVideoEmbedUrl` / `getVideoEmbedInfo` helpers now route
 * through it, so every consumer gets the hardened allowlist + `youtube-nocookie`
 * canonicalisation for free. Previously this module used substring host checks
 * (`hostname.includes('youtube.com')`, which also matched `youtube.com.evil`)
 * and emitted `www.youtube.com/embed/` — this file replaces all of that.
 *
 * The competition-livestream surface consumes this via
 * `@kit/auditions`'s `resolveLivestreamEmbed`, which is now a thin re-export.
 */

export type VideoPlatform = 'youtube' | 'vimeo' | 'generic';

export type VideoEmbedProvider =
  | 'youtube'
  | 'vimeo'
  | 'twitch'
  | 'facebook'
  | 'dailymotion';

export interface VideoEmbed {
  /** The canonical, sandbox-safe iframe `src` to render. */
  embedUrl: string;
  /** The matched provider, for a11y labels / analytics. */
  provider: VideoEmbedProvider;
}

/**
 * Allowlisted embed hosts. Keyed by canonical apex; the set already enumerates
 * every accepted subdomain. Only hosts that publish a documented, sandbox-safe
 * iframe embed endpoint belong here.
 */
const ALLOWED_HOSTS: ReadonlySet<string> = new Set([
  // YouTube
  'youtube.com',
  'www.youtube.com',
  'm.youtube.com',
  'youtu.be',
  'youtube-nocookie.com',
  'www.youtube-nocookie.com',
  // Vimeo
  'vimeo.com',
  'www.vimeo.com',
  'player.vimeo.com',
  // Twitch
  'twitch.tv',
  'www.twitch.tv',
  'player.twitch.tv',
  // Facebook / Meta live
  'facebook.com',
  'www.facebook.com',
  // Dailymotion
  'dailymotion.com',
  'www.dailymotion.com',
  'geo.dailymotion.com',
]);

function parseUrl(raw: string): URL | null {
  try {
    const url = new URL(raw.trim());
    // Only https — rejects javascript:, data:, http: schemes.
    if (url.protocol !== 'https:') return null;
    return url;
  } catch {
    return null;
  }
}

/**
 * Returns true when `host` is allowlisted (exact match against the set; the set
 * already enumerates the subdomains we accept). Lower-cased before comparison.
 */
export function isAllowedEmbedHost(host: string): boolean {
  return ALLOWED_HOSTS.has(host.toLowerCase());
}

/**
 * Validate + normalise a user-supplied video URL into a safe iframe src.
 * Returns `null` for any URL that is not https, not on the host allowlist, or
 * whose video id cannot be extracted for the matched provider. NEVER returns a
 * raw pass-through of the input.
 */
export function resolveVideoEmbed(
  raw: string | null | undefined,
): VideoEmbed | null {
  if (!raw) return null;
  const url = parseUrl(raw);
  if (!url) return null;

  const host = url.hostname.toLowerCase();
  if (!isAllowedEmbedHost(host)) return null;

  // ── YouTube ──
  if (host === 'youtu.be') {
    const id = url.pathname.replace(/^\//, '').split('/')[0] ?? '';
    if (!/^[\w-]{6,}$/.test(id)) return null;
    return {
      embedUrl: `https://www.youtube-nocookie.com/embed/${id}`,
      provider: 'youtube',
    };
  }
  if (host.endsWith('youtube.com') || host.endsWith('youtube-nocookie.com')) {
    // Accept /watch?v=, /live/<id>, /embed/<id>, /shorts/<id>.
    let id = url.searchParams.get('v') ?? '';
    if (!id) {
      const m = url.pathname.match(/^\/(?:live|embed|shorts)\/([\w-]{6,})/);
      id = m?.[1] ?? '';
    }
    if (!/^[\w-]{6,}$/.test(id)) return null;
    return {
      embedUrl: `https://www.youtube-nocookie.com/embed/${id}`,
      provider: 'youtube',
    };
  }

  // ── Vimeo ──
  if (host === 'player.vimeo.com') {
    const m = url.pathname.match(/^\/video\/(\d+)/);
    if (!m) return null;
    return {
      embedUrl: `https://player.vimeo.com/video/${m[1]}`,
      provider: 'vimeo',
    };
  }
  if (host.endsWith('vimeo.com')) {
    // vimeo.com/<id> or vimeo.com/event/<id> (live events).
    const eventMatch = url.pathname.match(/^\/event\/(\d+)/);
    if (eventMatch) {
      return {
        embedUrl: `https://vimeo.com/event/${eventMatch[1]}/embed`,
        provider: 'vimeo',
      };
    }
    const m = url.pathname.match(/^\/(\d+)/);
    if (!m) return null;
    return {
      embedUrl: `https://player.vimeo.com/video/${m[1]}`,
      provider: 'vimeo',
    };
  }

  // ── Twitch ── (channel or video). Twitch embeds require a parent param,
  // which the renderer appends from the request host; here we keep the
  // canonical player URL with the channel/video selector only.
  if (host.endsWith('twitch.tv')) {
    const channelMatch = url.pathname.match(/^\/([\w]{2,})\/?$/);
    const videoMatch = url.pathname.match(/^\/videos\/(\d+)/);
    if (videoMatch) {
      return {
        embedUrl: `https://player.twitch.tv/?video=v${videoMatch[1]}`,
        provider: 'twitch',
      };
    }
    if (channelMatch && channelMatch[1] !== 'videos') {
      return {
        embedUrl: `https://player.twitch.tv/?channel=${channelMatch[1]}`,
        provider: 'twitch',
      };
    }
    if (
      host === 'player.twitch.tv' &&
      (url.searchParams.has('channel') || url.searchParams.has('video'))
    ) {
      const channel = url.searchParams.get('channel');
      const video = url.searchParams.get('video');
      const q = channel ? `channel=${channel}` : `video=${video}`;
      return { embedUrl: `https://player.twitch.tv/?${q}`, provider: 'twitch' };
    }
    return null;
  }

  // ── Dailymotion ──
  if (host.endsWith('dailymotion.com')) {
    const m = url.pathname.match(/^\/(?:embed\/)?video\/([\w]+)/);
    if (!m) return null;
    return {
      embedUrl: `https://geo.dailymotion.com/player.html?video=${m[1]}`,
      provider: 'dailymotion',
    };
  }

  // ── Facebook live ── (plugin embed). Facebook requires the full source URL
  // wrapped in the video plugin; we only accept https facebook.com URLs and
  // wrap them through the official plugin endpoint.
  if (host.endsWith('facebook.com')) {
    return {
      embedUrl: `https://www.facebook.com/plugins/video.php?href=${encodeURIComponent(url.href)}`,
      provider: 'facebook',
    };
  }

  return null;
}

/**
 * Back-compat alias. The competition-livestream surface historically imported
 * this name; it and `resolveVideoEmbed` are the same hardened resolver.
 */
export const resolveLivestreamEmbed = resolveVideoEmbed;

/**
 * Detect the video platform from a URL (YouTube / Vimeo / generic). Lightweight
 * categoriser for the two dominant providers; use `resolveVideoEmbed` for the
 * full allowlist + embed-URL construction.
 */
export function getVideoPlatform(url: string): VideoPlatform {
  try {
    const urlObj = new URL(url);
    const hostname = urlObj.hostname.toLowerCase();

    if (hostname.includes('youtube.com') || hostname.includes('youtu.be')) {
      return 'youtube';
    }
    if (hostname.includes('vimeo.com')) {
      return 'vimeo';
    }
    return 'generic';
  } catch {
    return 'generic';
  }
}

/**
 * Extract the video ID from an allowlisted YouTube or Vimeo URL. Returns null
 * for non-https, non-allowlisted, or id-less URLs. Used for thumbnail lookups
 * where only the bare id is needed.
 */
export function extractVideoId(url: string): string | null {
  const parsed = parseUrl(url);
  if (!parsed) return null;
  const hostname = parsed.hostname.toLowerCase();
  if (!isAllowedEmbedHost(hostname)) return null;

  // YouTube patterns
  if (hostname === 'youtu.be') {
    return parsed.pathname.slice(1).split('/')[0] || null;
  }
  if (
    hostname.endsWith('youtube.com') ||
    hostname.endsWith('youtube-nocookie.com')
  ) {
    const watchId = parsed.searchParams.get('v');
    if (watchId) return watchId;
    const pathMatch = parsed.pathname.match(/\/(shorts|embed|live)\/([^/?]+)/);
    if (pathMatch?.[2]) return pathMatch[2];
    return null;
  }

  // Vimeo patterns
  if (hostname.endsWith('vimeo.com')) {
    const match = parsed.pathname.match(/\/(?:video\/)?(\d+)/);
    return match?.[1] ?? null;
  }

  return null;
}

/**
 * Get the canonical embed URL for a video given its original URL. The second
 * `videoId` argument is retained for signature compatibility but ignored — the
 * URL alone drives the hardened resolver.
 */
export function getEmbedUrl(
  originalUrl: string,
  _videoId?: string,
): string | null {
  return resolveVideoEmbed(originalUrl)?.embedUrl ?? null;
}

/**
 * Check if a URL can be safely embedded (allowlisted YouTube / Vimeo / etc.).
 */
export function isEmbeddable(url: string): boolean {
  return resolveVideoEmbed(url) !== null;
}

/**
 * Get the embed URL directly from a video URL (convenience). Returns null if
 * the URL is not embeddable.
 */
export function getVideoEmbedUrl(url: string): string | null {
  return resolveVideoEmbed(url)?.embedUrl ?? null;
}

/**
 * Video embed info with all relevant data.
 */
export interface VideoEmbedInfo {
  platform: VideoPlatform;
  videoId: string | null;
  embedUrl: string | null;
  isEmbeddable: boolean;
}

/**
 * Get complete video embed information from a URL.
 */
export function getVideoEmbedInfo(url: string): VideoEmbedInfo {
  const platform = getVideoPlatform(url);
  const videoId = extractVideoId(url);
  const embedUrl = getVideoEmbedUrl(url);

  return {
    platform,
    videoId,
    embedUrl,
    isEmbeddable: embedUrl !== null,
  };
}
